import { List, Map } from 'immutable';

import { shapeFaireDistanceUnitForApp, shapeFaireMassUnitForApp, shapeFairePricesForApp, shapeFaireShippingAttributeForApp, shapeKeyDate } from '../product/shapeAttributes';
import { findAnalogue, findAnalogueScale, getAvailable } from '../taxonomy';
import { setAllInfluenced, updateInfluence } from './influence';
import { isValidNumber, toFixed, toString } from '../number';
import { getRetainFromVariations } from './retain';
import { getDeletedCombinations } from './combinations';
import { newVariations } from '../product/new';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { COMBINATIONS, DELETED, INFLUENCE, RETAIN, VARIANTS } from '../../constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { CUSTOM_PROPERTY_IDS } from '../../constants/taxonomy';
import { AMOUNT, REQUIRED, VALUE } from '../../constants/product';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

function shapeEbayVariationsForApp({ asProfile, data, product }) {
  const unset = asProfile ? null : '';

  function getOptionPhotos({ option }) {
    function reduceOptionPhotos(result, photo) {
      const url = get('fullsize_url')(photo);
      return url
        ? result.push(Map({ fullsize_url: url }))
        : result;
    }

    return (get('photos')(option) || []).reduce(reduceOptionPhotos, EMPTY.LIST);
  }

  function reduceEbayOptions(result, option, optionIndex) {
    function reduceValues(options, value) {
      const item = { value: get('value')(value) };

      if (!asProfile) {
        item.photos = getOptionPhotos({ option: value });
      }

      return options.push(Map(item));
    }

    const variant = {
      id: -1 - optionIndex,
      name: get('name')(option),
      options: get('value')(option).reduce(reduceValues, EMPTY.LIST),
    };

    return result.set(VARIANTS, result.get(VARIANTS).push(Map(variant)));
  }

  function reduceEbayVariants(result, variant, variantIndex) {
    const [id, options, price, quantity, sku] = get('id', 'options', 'price', 'quantity', 'sku')(variant);

    function reduceOptions(items, item) {
      return items.push(Map({ name: get('value')(item) }));
    }

    const combination = Map({
      [ATTRIBUTE.PRICE]: toFixed(price, 2) || unset,
      [ATTRIBUTE.QUANTITY]: toString(quantity) || unset,
      [ATTRIBUTE.SKU]: sku || unset,
      id: id || -1 - variantIndex,
      options: options.reduce(reduceOptions, EMPTY.LIST),
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  let variations = newVariations({ channel: EBAY });
  variations = reduce(get('options')(data), variations, reduceEbayOptions);
  variations = reduce(get('variants')(data), variations, reduceEbayVariants);

  if (asProfile) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: EBAY, variations }));
  } else {
    variations = variations.setIn([REQUIRED, ATTRIBUTE.SKU], product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, REQUIRED]));
  }

  return variations;
}

function shapeEtsyVariationsForApp({ asProfile, data, imagesById, product }) {
  const unset = asProfile ? null : '';

  function getOptionImageHash(option) {
    const [imageHash, channelImageId, velaImageId] = get('imageHash', 'channelImageId', 'velaImageId')(option);
    let result;

    function findImage(image) {
      return get('channel_image_id')(image) === channelImageId;
    }

    function findPhoto(value, key) {
      return function findItem(item) {
        return item.get(key) === value;
      };
    }

    if (imageHash) {
      result = imageHash;
    } else if (imagesById) {
      if (velaImageId) {
        result = get([velaImageId, 'hash'])(imagesById);
      } else if (channelImageId) {
        const sameImage = find(imagesById, findImage);

        if (sameImage) {
          result = get('hash')(sameImage);
        }
      }
    } else if ((velaImageId || channelImageId) && getSize(product.getIn([FORM.PHOTOS, VALUE])) > 0) {
      const value = velaImageId || channelImageId;
      const key = velaImageId ? 'id' : 'channel_image_id';
      const photo = product.getIn([FORM.PHOTOS, VALUE]).find(findPhoto(value, key));

      if (photo) {
        result = photo.get('hash') || null;
      }
    }

    return result || null;
  }

  function fixCustomProperty({ channel, property, variationIndex }) {
    return property === CUSTOM_PROPERTY_IDS[channel][variationIndex]
      ? property
      : CUSTOM_PROPERTY_IDS[channel][variationIndex];
  }

  function reduceEtsyVariations(result, variation) {
    const name = get('formattedName')(variation);

    if (!name) return result;

    function mapOptions(option) {
      const [id, value, valueId] = get('id', 'value', 'valueId')(option);
      return Map({
        id,
        value,
        valueId,
        imageHash: asProfile ? null : getOptionImageHash(option),
      });
    }

    let influence = result.get(INFLUENCE);

    const [
      first,
      id,
      influencesPrice = false,
      influencesQuantity = false,
      influencesProcessingProfile = false,
      influencesSku = false,
      options,
      propertyId,
      scalingOptionId,
    ] = get(
      'first',
      'id',
      'influencesPrice',
      'influencesQuantity',
      'influencesReadinessState',
      'influencesSku',
      'options',
      'propertyId',
      'scalingOptionId',
    )(variation);

    const variationIndex = first ? 0 : 1;

    if (influencesPrice) {
      influence = influence.setIn([ATTRIBUTE.PRICE, variationIndex], true);
    }

    if (influencesProcessingProfile) {
      influence = influence.setIn([ATTRIBUTE.PROCESSING_PROFILE, variationIndex], true);
    }

    if (influencesQuantity) {
      influence = influence.setIn([ATTRIBUTE.QUANTITY, variationIndex], true);
    }

    if (influencesSku) {
      influence = influence.setIn([ATTRIBUTE.SKU, variationIndex], true);
    }

    const category = get('taxonomyId')(data);
    const scale = isValidNumber(scalingOptionId) ? parseInt(scalingOptionId) : undefined;
    const property = isValidNumber(propertyId) ? parseInt(propertyId) : undefined;
    let variant = Map({
      id,
      name,
      options: List(options.map(mapOptions)),
      property,
    });

    if (category && category !== AMOUNT.ZERO) {
      if (!CUSTOM_PROPERTY_IDS[ETSY] || !CUSTOM_PROPERTY_IDS[ETSY].includes(property)) {
        const { availableScales, availableVariations } = getAvailable({ category, channel: ETSY, property, scale });
        const sameVariation = availableVariations.find(findAnalogue({ id: property }));

        if (sameVariation) {
          if (sameVariation.name !== name) {
            variant = variant.set('name', sameVariation.name);
          }
        } else {
          const analogueVariation = availableVariations.find(findAnalogue({ name }));

          if (analogueVariation && analogueVariation.id !== property) {
            variant = variant.set('property', analogueVariation.id);
          }
        }

        if (getSize(availableScales)) {
          if (!scale) {
            variant = variant.set('scale', 0);
          } else {
            const analogueScale = availableScales.find(findAnalogueScale({ channel: ETSY, scale }));

            if (analogueScale && analogueScale.id !== scale) {
              variant = variant.set('scale', analogueScale.id);
            } else {
              variant = variant.set('scale', scale);
            }
          }
        } else if (scale) {
          variant = variant.set('scale', scale);
        }
      } else {
        variant = variant.set('property', fixCustomProperty({ channel: ETSY, property, variationIndex }));
      }
    } else if (CUSTOM_PROPERTY_IDS[ETSY] && CUSTOM_PROPERTY_IDS[ETSY].includes(property)) {
      variant = variant.set('property', fixCustomProperty({ channel: ETSY, property, variationIndex }));
    }

    return result
      .set(INFLUENCE, influence)
      .set(VARIANTS, first ? result.get(VARIANTS).unshift(variant) : result.get(VARIANTS).push(variant));
  }

  function reduceEtsyOfferings(result, offering) {
    const [
      id,
      price,
      quantity,
      sku,
      visibility,
      readinessStateId,
      readiness_state_id,
      variationOptions,
    ] = get('id', 'price', 'quantity', 'sku', 'visibility', 'readinessStateId', 'readiness_state_id', 'variationOptions')(offering);

    const processingProfile = readinessStateId !== undefined && readinessStateId !== null
      ? readinessStateId
      : readiness_state_id !== undefined
        ? readiness_state_id
        : null;

    if (!getSize(variationOptions)) return result;

    function mapOptions(option) {
      const [optionId, variantId] = get('optionId', 'variationId')(option);

      function findVariantById(variant) {
        return variantId === variant.get('id');
      }

      function findOptionById(variantOption) {
        return optionId === variantOption.get('id');
      }

      const sameVariant = result.get(VARIANTS).find(findVariantById);
      const options = sameVariant.get('options');
      const sameOption = options.find(findOptionById);
      const name = sameOption.get('value');

      return Map({ name, optionId, variantId });
    }

    const combination = Map({
      id,
      options: List(variationOptions.map(mapOptions)),
      [ATTRIBUTE.PRICE]: toFixed(price, 2) || unset,
      [ATTRIBUTE.QUANTITY]: toString(quantity) || unset,
      [ATTRIBUTE.SKU]: sku || unset,
      [ATTRIBUTE.PROCESSING_PROFILE]: processingProfile || unset,
      [ATTRIBUTE.VISIBILITY]: visibility,
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  let variations = newVariations({ channel: ETSY });
  variations = reduce(get('variations')(data), variations, reduceEtsyVariations);
  variations = reduce(get('offerings')(data), variations, reduceEtsyOfferings);
  variations = setAllInfluenced(variations);

  if (asProfile) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: ETSY, variations }));
  }

  return variations;
}

function shapeFaireVariationsForApp({ asProfile, data, product }) {
  const unset = asProfile ? null : '';

  function reduceFaireOptions(result, option, optionIndex) {
    function reduceValues(options, value) {
      const item = { value: get('value')(value) };

      return options.push(Map(item));
    }

    const variant = {
      id: -1 - optionIndex,
      name: get('name')(option),
      options: get('value')(option).reduce(reduceValues, EMPTY.LIST),
    };

    return result.set(VARIANTS, result.get(VARIANTS).push(Map(variant)));
  }

  function reduceFaireVariants(result, variant, variantIndex) {
    const [
      quantity,
      restockDate,
      gtin,
      imageUrl,
      options,
      orderabilityType,
      startDate,
      endDate,
      deadlineDate,
      sku,
      tariffCode,
    ] = get(
      'available_quantity',
      'backordered_until',
      'gtin',
      'image_url',
      'options',
      'orderability_type',
      'preorder_details_expected_ship_date',
      'preorder_details_expected_ship_window_end_date',
      'preorder_details_stop_selling_at',
      'sku',
      'tariff_code',
    )(variant);

    function reduceOptions(items, item) {
      return items.push(Map({ name: get('value')(item) }));
    }

    const combination = Map({
      id: -1 - variantIndex,
      imageUrl,
      options: options.reduce(reduceOptions, EMPTY.LIST),
      [ATTRIBUTE.DEADLINE_DATE]: shapeKeyDate(deadlineDate),
      [ATTRIBUTE.DISTANCE_UNIT]: shapeFaireDistanceUnitForApp(get('measurements_distance_unit')(variant)),
      [ATTRIBUTE.END_DATE]: shapeKeyDate(endDate),
      [ATTRIBUTE.GTIN]: gtin || unset,
      [ATTRIBUTE.HEIGHT]: shapeFaireShippingAttributeForApp({ attribute: 'height', product: variant, unset }),
      [ATTRIBUTE.LENGTH]: shapeFaireShippingAttributeForApp({ attribute: 'length', product: variant, unset }),
      [ATTRIBUTE.MASS_UNIT]: shapeFaireMassUnitForApp(get('measurements_mass_unit')(variant)),
      [ATTRIBUTE.PREORDER]: orderabilityType === 'PREORDER',
      [ATTRIBUTE.QUANTITY]: toString(quantity) || unset,
      [ATTRIBUTE.RESTOCK_DATE]: restockDate ? new Date(restockDate) : null,
      [ATTRIBUTE.RETAIL_PRICE]: shapeFairePricesForApp({
        attribute: ATTRIBUTE.RETAIL_PRICE,
        product: variant,
        unset,
      }),
      [ATTRIBUTE.SKU]: sku || unset,
      [ATTRIBUTE.START_DATE]: shapeKeyDate(startDate),
      [ATTRIBUTE.TARIFF_CODE]: tariffCode || unset,
      [ATTRIBUTE.WEIGHT]: shapeFaireShippingAttributeForApp({ attribute: 'weight', product: variant, unset }),
      [ATTRIBUTE.WHOLESALE_PRICE]: shapeFairePricesForApp({
        attribute: ATTRIBUTE.WHOLESALE_PRICE,
        product: variant,
        unset,
      }),
      [ATTRIBUTE.WIDTH]: shapeFaireShippingAttributeForApp({ attribute: 'width', product: variant, unset }),
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  let variations = newVariations({ channel: FAIRE });
  variations = reduce(get('options')(data), variations, reduceFaireOptions);
  variations = reduce(get('variants')(data), variations, reduceFaireVariants);
  variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });

  if (asProfile) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: FAIRE, variations }));
  } else {
    variations = variations.setIn([REQUIRED, ATTRIBUTE.PRICE],
      product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, REQUIRED])
    );
  }

  return variations;
}

function shapeShopifyVariationsForApp({ asProfile, data, imagesById, product }) {
  const unset = asProfile ? null : '';

  function getCombinationImageHashAndImageId(variant) {
    const result = {
      imageId: null,
      imageHash: null,
    };

    if (asProfile) return result;

    const [
      velaImageId,
      imageHash,
      shopifyImageId,
    ] = get('vela_image_id', 'shopify_image_hash', 'shopify_image_id')(variant);

    function findPhoto(photo) {
      return photo.get('id') === velaImageId;
    }

    if (imageHash) {
      result.imageHash = imageHash;
    } else if (shopifyImageId) {
      result.imageId = shopifyImageId;
    } else if (velaImageId) {
      if (imagesById) {
        const [channelImageId, hash] = get([velaImageId, 'channel_image_id'], [velaImageId, 'hash'])(imagesById);

        if (channelImageId) {
          result.imageId = channelImageId;
        } else if (hash) {
          result.imageHash = hash;
        }
      } else if (getSize(product.getIn([FORM.PHOTOS, VALUE])) > 0) {
        const photo = product.getIn([FORM.PHOTOS, VALUE]).find(findPhoto);

        if (photo) {
          const channelImageId = photo.get('channel_image_id');
          const hash = photo.get('hash');

          if (channelImageId) {
            result.imageId = channelImageId;
          } else if (hash) {
            result.imageHash = hash;
          }
        }
      }
    }

    return result;
  }

  function reduceShopifyOptions(result, option, optionIndex) {
    const id = get('id')(option);
    const name = get('shopify_name')(option);
    const values = get('shopify_values')(option);
    const attribute = get('shopify_taxonomy_attribute_id')(option);

    if (!name) return result;

    function mapValues(value) {
      return Map({ value: value.trim() });
    }

    const variant = {
      attribute: attribute || null,
      id: id || -1 - optionIndex,
      name,
      options: List(values.map(mapValues)),
    };

    return result.set(VARIANTS, result.get(VARIANTS).push(Map(variant)));
  }

  function reduceShopifyVariants(result, variant, variantIndex) {
    function reduceOptions(options, name) {
      return name
        ? options.push(Map({ name: name.trim() }))
        : options;
    }

    const [
      id,
      sku,
      cpi,
      price,
      weight,
      option1,
      option2,
      option3,
      barcode,
      unit,
      chargeTax,
      cap,
      physical,
      quantity,
      hsCode,
      inventoryPolicy,
      countryCode,
      inventoryManagement,
    ] = get(
      'id',
      'shopify_sku',
      'shopify_cost',
      'shopify_price',
      'shopify_weight',
      'shopify_option1',
      'shopify_option2',
      'shopify_option3',
      'shopify_barcode',
      'shopify_weight_unit',
      'shopify_taxable',
      'shopify_compare_at_price',
      'shopify_requires_shipping',
      'shopify_inventory_quantity',
      'shopify_harmonized_system_code',
      'shopify_inventory_policy',
      'shopify_country_code_of_origin',
      'shopify_inventory_management',
    )(variant);

    const combination = Map({
      [ATTRIBUTE.BARCODE]: barcode || unset,
      [ATTRIBUTE.CAP]: toFixed(cap, 2) || unset,
      [ATTRIBUTE.CHARGE_TAX]: chargeTax,
      [ATTRIBUTE.CONTINUE_SELLING]: inventoryPolicy === 'continue',
      [ATTRIBUTE.COUNTRY_CODE]: countryCode || unset,
      [ATTRIBUTE.CPI]: toFixed(cpi, 2) || unset,
      [ATTRIBUTE.HS_CODE]: hsCode || unset,
      [ATTRIBUTE.PHYSICAL]: physical,
      [ATTRIBUTE.PRICE]: toFixed(price, 2) || unset,
      [ATTRIBUTE.QUANTITY]: quantity || unset,
      [ATTRIBUTE.SKU]: sku || unset,
      [ATTRIBUTE.TRACK_QUANTITY]: inventoryManagement === SHOPIFY,
      [ATTRIBUTE.WEIGHT]: weight || unset,
      [ATTRIBUTE.UNIT]: unit || unset,
      id: id || -1 - variantIndex,
      options: [option1, option2, option3].reduce(reduceOptions, EMPTY.LIST),
      ...getCombinationImageHashAndImageId(variant),
    });

    return result.set(COMBINATIONS, result.get(COMBINATIONS).push(combination));
  }

  let variations = newVariations({ channe: SHOPIFY });
  variations = reduce(get('options')(data), variations, reduceShopifyOptions);
  variations = reduce(get('variants')(data), variations, reduceShopifyVariants);
  variations = variations.set(DELETED, getDeletedCombinations({ variations }));

  if (asProfile) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: SHOPIFY, variations }));
  }

  return variations;
}

export function shapeVariationsForApp({ asProfile, data, imagesById, product }) {
  const channel = product.get('channel');

  switch (channel) {
    case EBAY: {
      return shapeEbayVariationsForApp({ asProfile, data, product });
    }

    case ETSY: {
      return shapeEtsyVariationsForApp({ asProfile, data, imagesById, product });
    }

    case FAIRE: {
      return shapeFaireVariationsForApp({ asProfile, data, product });
    }

    case SHOPIFY: {
      return shapeShopifyVariationsForApp({ asProfile, data, imagesById, product });
    }

    default: {
      let variations = newVariations({ channel });

      if (asProfile) {
        variations = variations.set(RETAIN, getRetainFromVariations({ channel, variations }));
      }

      return variations;
    }
  }
}
